import type { StoreState, UserPreferences } from "./types";
import type { ExperimentId } from "@globals";
import { merge } from "lodash-es";
import { create } from "zustand";
import { persist } from "zustand/middleware";

export const localStorageKey = "sdlabs-global-store";
const excludeFromPersistence: Array<keyof StoreState> = ["experimentStatus"];

export function createInitialUserPreferences(): UserPreferences {
  return {
    experiment: {
      alwaysShowLatestInsights: false
    }
  };
}

export const useGlobalStore = create<StoreState>()(
  // Persisting the global store to local storage.
  persist(
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    _ => ({
      user: null,
      selectedTeam: null, // ID of the selected team.
      experimentStatus: new Map(),
      userPreferences: createInitialUserPreferences(),
      dismissedCortexWarningIds: [],
      agentChatSettings: {
        open: false,
        chatMode: "sidebar",
        sidebarWidth: "25vw"
      }
    }),
    {
      name: localStorageKey,
      partialize: state =>
        Object.fromEntries(
          Object.entries(state).filter(([key]) => !excludeFromPersistence.includes(key as keyof StoreState))
        ),
      merge: (persistedState, currentState) => merge(currentState, persistedState)
    }
  )
);

export const useUserStore = () => useGlobalStore(state => state.user);

/** Subscribe to status updates of a specific experiment. */
export const useExperimentStatus = (id: ExperimentId) => useGlobalStore(state => state.experimentStatus.get(id));

/** Subscribe to status updates of any experiment (returns the full map). */
export const useExperimentStatusMap = () => useGlobalStore(state => state.experimentStatus);

export const useUserPreferences = () => useGlobalStore(state => state.userPreferences);
export const useDismissedCortexWarningIds = () => useGlobalStore(state => state.dismissedCortexWarningIds);

export const useSelectedTeam = () => useGlobalStore(state => state.selectedTeam);

export const useAgentChatSettings = () => useGlobalStore(state => state.agentChatSettings);
